import React from "react";
import { Building2, MapPin, Phone, FileText, Landmark, Copy, UserRound, Scale } from "lucide-react";
import { toast } from "sonner";
import RichText from "@/components/legal/RichText";

const ICONS = { user: UserRound, building: Building2, pin: MapPin, phone: Phone, file: FileText, scale: Scale, bank: Landmark };
const linkCls = "text-ink hover:text-brand transition-colors";

const Card = ({ icon: Icon, label, testId, className = "", children }) => (
  <div data-testid={testId} className={`rounded-2xl border border-line bg-surface p-6 md:p-7 ${className}`}>
    <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] font-semibold text-faint mb-4">
      <Icon size={14} strokeWidth={1.8} className="text-brand" /> {label}
    </p>
    <div className="text-sm text-dim leading-relaxed">{children}</div>
  </div>
);

const copy = (v) => navigator.clipboard.writeText(v.replace(/\s/g, "")).then(() => toast.success("IBAN kopiert"));

const Value = ({ row, tid }) => {
  if (row.href) {
    const ext = row.href.startsWith("http");
    return <a href={row.href} className={linkCls} data-testid={tid && `${tid}-link`} {...(ext ? { target: "_blank", rel: "noopener noreferrer" } : {})}>{row.v}</a>;
  }
  if (!row.copy) return <RichText text={row.v} />;
  return (
    <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 tabular-nums tracking-wider">
      <span data-testid={tid}>{row.v}</span>
      <button type="button" onClick={() => copy(row.v)} aria-label="IBAN kopieren" data-testid={`${tid}-copy-btn`}
        className="inline-flex items-center gap-1 text-xs tracking-normal text-faint hover:text-brand transition-colors">
        <Copy size={13} /> Kopieren
      </button>
    </span>
  );
};

const Rows = ({ rows, wide }) => (
  <dl>
    {rows.map((r) => {
      const tid = r.id && `impressum-${r.id}`;
      return (
        <div key={r.k} className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-4 py-2.5 border-b border-line/70 last:border-0">
          <dt className={`${wide ? "sm:w-56" : "sm:w-36"} shrink-0 text-xs text-faint`}>{r.k}</dt>
          <dd className="text-ink font-medium" data-testid={r.copy ? undefined : tid}><Value row={r} tid={tid} /></dd>
        </div>
      );
    })}
  </dl>
);

export const LegalCards = ({ cards }) => (
  <div className="grid md:grid-cols-2 gap-4 md:gap-5">
    {cards.map((c) => (
      <Card key={c.id} icon={ICONS[c.icon] || FileText} label={c.label} testId={`impressum-${c.id}`} className={c.wide ? "md:col-span-2" : ""}>
        {c.title && <p className={c.big ? "font-display font-bold text-2xl text-ink tracking-tight" : "text-ink font-medium"}>{c.title}</p>}
        {c.lines && <p className={c.big ? "mt-1" : ""}>{c.lines.map((l, i) => <React.Fragment key={l}>{i > 0 && <br />}{l}</React.Fragment>)}</p>}
        {c.note && <p className="text-xs text-faint mt-2">{c.note}</p>}
        {c.rows && <Rows rows={c.rows} wide={c.wide} />}
      </Card>
    ))}
  </div>
);

export default LegalCards;
